import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";

export default function WeeklyHoursPage() {
  const [weekStart, setWeekStart] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async (ws) => {
    setLoading(true);
    try {
      const { data } = await api.get("/time-entries/weekly", { params: ws ? { week_start: ws } : {} });
      setRows(data.rows);
      setWeekStart(data.week_start);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const jump = (deltaDays) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + deltaDays);
    load(d.toISOString().slice(0, 10));
  };

  return (
    <div className="space-y-6" data-testid="weekly-hours-page">
      <div className="flex items-end flex-wrap justify-between gap-3">
        <div>
          <div className="tiny-label">Weekly view</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Hours by employee</h1>
          <p className="text-muted-foreground mt-1">Review worked hours, overtime, and holidays for the selected week.</p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label>Week starting</Label>
            <Input type="date" value={weekStart} onChange={(e) => load(e.target.value)} className="w-48" data-testid="week-start-picker" />
          </div>
          <Button variant="outline" onClick={() => jump(-7)} data-testid="week-prev">← Prev</Button>
          <Button variant="outline" onClick={() => jump(7)} data-testid="week-next">Next →</Button>
        </div>
      </div>

      <Card className="border-border shadow-none">
        <div className="overflow-x-auto app-scroll">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left tiny-label">
                <th className="px-4 py-3">Employee</th>
                <th className="px-4 py-3 text-right">Days</th>
                <th className="px-4 py-3 text-right">Worked</th>
                <th className="px-4 py-3 text-right">Expected</th>
                <th className="px-4 py-3 text-right">Extra</th>
                <th className="px-4 py-3 text-right">Missing</th>
                <th className="px-4 py-3 text-right">Holiday</th>
                <th className="px-4 py-3">Flag</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.employee.id} className="border-t border-border hover:bg-muted/30" data-testid={`week-row-${r.employee.employee_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{r.employee.full_name}</div>
                    <div className="text-xs text-muted-foreground">{r.employee.department || "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono">{r.days_worked}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">{r.total_hours.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right font-mono text-muted-foreground">{r.expected_hours.toFixed(2)}</td>
                  <td className={`px-4 py-3 text-right font-mono ${r.extra_hours > 0 ? "text-amber-700 font-semibold" : ""}`}>{r.extra_hours.toFixed(2)}</td>
                  <td className={`px-4 py-3 text-right font-mono ${r.missing_hours > 0 ? "text-red-700" : ""}`}>{r.missing_hours.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right font-mono">{r.holiday_days.toFixed(2)}</td>
                  <td className="px-4 py-3">
                    {r.extra_hours > 0 && <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50">Overtime</Badge>}
                    {r.missing_hours > 0 && <Badge variant="outline" className="text-red-700 border-red-200 bg-red-50">Short</Badge>}
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">No data for this week.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
